import {autoDuration, humanFriendlyInt, thousandSeparatedInt} from "@/lib/format.tsx";
import {MetricGranularity, MetricSeries, MetricSeriesKind} from "@/lib/types/metricsExplorer.ts";
import {ExplorerChartType} from "@/pages/metricsExplorer/explorerConfig.ts";

const MILLISECONDS_PER_SECOND = 1000;

const SERIES_LINE_WIDTH = 2;
//: Overlays are annotations on the data, so they sit visually behind it.
const OVERLAY_LINE_WIDTH = 1;

/** A comparison period is context for the current one, so it sits behind it. */
const COMPARISON_OPACITY = 0.6;
/** Anything that is the data itself draws at full strength. */
const FULL_OPACITY = 1;

const GRANULARITY_MILLIS: Record<MetricGranularity, number> = {
  AUTO: 0,
  ONE_MINUTE: 60_000,
  FIFTEEN_MINUTES: 900_000,
  ONE_HOUR: 3_600_000,
  ONE_DAY: 86_400_000,
  ONE_WEEK: 604_800_000,
  ONE_MONTH: 2_592_000_000,
};

/**
 * How far apart two points may be before the line breaks.
 *
 * The server returns one point per populated bucket and omits empty ones, so a bucket the
 * check never reported in is simply absent rather than null — Highcharts would otherwise
 * draw a straight line across it and imply data that does not exist.
 *
 * Adjacent buckets sit exactly one interval apart, so the threshold has to clear that
 * without reaching two. The half-interval margin absorbs a day that is 23 or 25 hours long
 * across a DST boundary, which would otherwise break every line on the last Sunday of
 * October.
 *
 * Returns 0 for AUTO, which Highcharts reads as "never break". The response always names
 * the granularity it actually used, so this only applies before the first result arrives.
 */
export const gapSizeFor = (granularity: MetricGranularity): number => GRANULARITY_MILLIS[granularity] * 1.5;

/**
 * Highcharts series type per time-series chart type. Overlays ignore this: a trend line is
 * a line whatever the data underneath is drawn as. The reshaping chart types (scatter,
 * distribution, heatmap) read the data differently and build their own options.
 */
const HIGHCHARTS_TYPE: Record<string, string> = {
  line: "line",
  area: "area",
  stackedArea: "area",
  column: "column",
  stackedColumn: "column",
};

const STACKED_TYPES: ExplorerChartType[] = ["stackedArea", "stackedColumn"];

export const isStacked = (chartType: ExplorerChartType): boolean => STACKED_TYPES.includes(chartType);

/** Overlays the analysis engine computed, as opposed to series that were queried. */
const DERIVED_KINDS: MetricSeriesKind[] = ["TREND", "MOVING_AVERAGE", "BASELINE_UPPER", "BASELINE_LOWER"];

export const isDerived = (kind: MetricSeriesKind): boolean => DERIVED_KINDS.includes(kind);

/**
 * Format a value for its unit.
 *
 * Units come from the metric catalog, so a new metric formats correctly without a change
 * here as long as it declares one of these. Anything unrecognised falls back to a plain
 * number rather than guessing.
 */
export const formatMetricValue = (value: number | null, unit: string | null): string => {
  if (value === null) {
    return "—";
  }
  switch (unit) {
    case "seconds":
      return autoDuration(value);
    case "milliseconds":
      return `${Math.round(value)}ms`;
    case "bytes":
      return formatBytes(value);
    case "days":
      return `${Math.round(value)}d`;
    case "score":
      return value.toFixed(2);
    default:
      return Number.isInteger(value) ? thousandSeparatedInt(value) : value.toFixed(3);
  }
};

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"];

const formatBytes = (value: number): string => {
  let scaled = value;
  let unitIndex = 0;
  while (scaled >= 1024 && unitIndex < BYTE_UNITS.length - 1) {
    scaled /= 1024;
    unitIndex += 1;
  }
  return `${unitIndex === 0 ? Math.round(scaled) : scaled.toFixed(1)}${BYTE_UNITS[unitIndex]}`;
};

export const formatAxisValue = (value: number, unit: string | null): string =>
  unit === null && Math.abs(value) >= 10000 ? humanFriendlyInt(value) : formatMetricValue(value, unit);

/**
 * Human-readable series name.
 *
 * The check id only appears when a series spans more than one check, and the metric label
 * only when more than one metric is on the chart — otherwise every legend entry repeats
 * the same words and the part that actually differs gets lost.
 */
export const buildSeriesName = (
  series: MetricSeries,
  checkNames: Record<number, string>,
  showCheck: boolean,
  showMetric: boolean,
  /** The builder row's letter, which ties a line to the row that drew it. */
  letter?: string
): string => {
  const parts: string[] = letter ? [letter] : [];
  if (showCheck) {
    parts.push(checkNames[series.check_id] ?? `Check ${series.check_id}`);
  }
  if (showMetric) {
    parts.push(series.label);
  }
  series.dimensions.forEach((dimension) => {
    parts.push(dimension.name === "step_number" ? `Step ${dimension.value}` : dimension.value);
  });
  if (parts.length === (letter ? 1 : 0)) {
    parts.push(series.label);
  }
  const name = parts.join(" · ");
  return series.kind === "COMPARISON" ? `${name} (previous)` : name;
};

/** Distinct y-axis per unit, so seconds and bytes never share a scale. */
export const collectUnits = (series: MetricSeries[]): (string | null)[] => {
  const units: (string | null)[] = [];
  series.forEach((item) => {
    if (!units.some((unit) => unit === item.unit)) {
      units.push(item.unit);
    }
  });
  return units;
};

export interface ChartPoint {
  x: number;
  y: number | null;
}

/**
 * Points as [timestamp, value].
 *
 * A comparison series is slid forward by its own offset so it lines up with the primary
 * period on a shared axis. The tooltip reads the real timestamp back off the series, so
 * shifting here does not misreport when a point happened.
 */
export const toChartPoints = (series: MetricSeries): ChartPoint[] =>
  series.datapoints.map((point) => ({
    x: Date.parse(point.time) + series.time_offset_seconds * MILLISECONDS_PER_SECOND,
    y: point.value,
  }));

export interface BuildOptionsParams {
  series: MetricSeries[];
  chartType: ExplorerChartType;
  checkNames: Record<number, string>;
  /** Builder row letters by series ref; left out where there are no rows, as on a dashboard. */
  seriesLetters?: Record<string, string>;
}

export interface ExplorerChartSeries {
  type: string;
  name: string;
  data: ChartPoint[];
  yAxis: number;
  dashStyle?: string;
  lineWidth: number;
  opacity: number;
  enableMouseTracking?: boolean;
  marker: {enabled?: boolean; enabledThreshold?: number; radius?: number};
  custom: {unit: string | null; kind: MetricSeriesKind; realOffsetSeconds: number};
}

/**
 * Map the API's series onto Highcharts series.
 *
 * Kept separate from the chart component so the mapping — which is where the subtle rules
 * live — can be tested without mounting Highcharts.
 */
export const buildChartSeries = ({
  series,
  chartType,
  checkNames,
  seriesLetters = {},
}: BuildOptionsParams): ExplorerChartSeries[] => {
  const units = collectUnits(series);
  const primaries = series.filter((item) => item.kind === "PRIMARY");
  const showCheck = new Set(primaries.map((item) => item.check_id)).size > 1;
  const showMetric = new Set(primaries.map((item) => item.metric)).size > 1;
  return series.map((item) => {
    const derived = isDerived(item.kind);
    const comparison = item.kind === "COMPARISON";
    const name = buildSeriesName(item, checkNames, showCheck, showMetric, seriesLetters[item.ref]);
    return {
      // Bars would obscure the line they annotate, so overlays always draw as lines.
      type: derived ? "line" : (HIGHCHARTS_TYPE[chartType] ?? "line"),
      name: derived ? `${name} · ${overlayLabel(item.kind)}` : name,
      data: toChartPoints(item),
      yAxis: Math.max(0, units.indexOf(item.unit)),
      dashStyle: derived || comparison ? "ShortDash" : undefined,
      // Always an explicit width. Passing `undefined` here does not fall back to the
      // Highcharts default — it reaches the renderer and draws a zero-width stroke, which
      // leaves the line invisible while the path itself is present and correct.
      lineWidth: derived ? OVERLAY_LINE_WIDTH : SERIES_LINE_WIDTH,
      // Always an explicit number, for the same reason as lineWidth but a worse failure.
      // Highcharts' Series.setState destructures opacity out of these options and only
      // overrides it while a state is set; leaving the legend restores the state "", which
      // is falsy, so an undefined opacity goes straight to group.animate() and tweens to
      // NaN. The series then renders with opacity="NaN" and never comes back.
      opacity: comparison ? COMPARISON_OPACITY : FULL_OPACITY,
      // Band edges are context, not data; letting them capture the pointer makes the
      // series underneath hard to inspect.
      enableMouseTracking: item.kind !== "BASELINE_UPPER" && item.kind !== "BASELINE_LOWER",
      // Left to Highcharts: it hides markers on dense series and shows them when points
      // are sparse. Forcing them off renders an isolated point — a check that reported a
      // handful of times in the window — as nothing at all.
      marker: {enabledThreshold: 2, radius: 3},
      custom: {unit: item.unit, kind: item.kind, realOffsetSeconds: item.time_offset_seconds},
    };
  });
};

const OVERLAY_LABELS: Partial<Record<MetricSeriesKind, string>> = {
  TREND: "trend",
  MOVING_AVERAGE: "moving avg",
  BASELINE_UPPER: "upper band",
  BASELINE_LOWER: "lower band",
};

const overlayLabel = (kind: MetricSeriesKind): string => OVERLAY_LABELS[kind] ?? kind.toLowerCase();
